import React, { useState } from 'react'
import { failureText, lmsAdmin } from '../api'
import { dateTimeText, utcText } from '../../../shared/date/local-time'
import { LmsIcon } from '../lms-icon'

// An LTI 1.3 connection's tool key (Phase L Task 8; L-4, B7), on
// signing-key-section.js's pattern. EdXR signs what it sends the LMS with it
// (grade returns and the course picked through Deep Linking); the LMS fetches
// the public half from the tool's keyset URL. The page shows the key's `kid`
// and when it was made, never its material. Rotate and Revoke each ask first:
//   - Rotate publishes the old and the new key together for 7 days, so an LMS
//     that cached the old set still verifies until it fetches the new one;
//   - Revoke takes every key out of the keyset at once, but an LMS that
//     cached the set may keep trusting the old key until its cache refreshes
//     (Moodle keeps a key until a check fails, B7), so the dialog says to
//     purge the LMS's cache after revoking, and that rotating is for routine
//     changes.
// Neither touches the package signing key: an LTI connection has none.

// The grace a rotated key keeps its place in the key set, as the service has it.
export const ROTATION_GRACE_DAYS = 7

const WARNING = {
  rotate: `A new key signs from now on. The current key stays in the keyset URL for ${ROTATION_GRACE_DAYS} days, so your LMS can still verify with it while it fetches the new one. Nothing needs to change in your LMS.`,
  revoke: `Every key of this connection leaves the keyset URL at once, and a new key signs from now on. Your LMS may keep using a copy of the old key set until its cache refreshes, so until then it can still accept something signed with the old key (Moodle keeps a cached key until a check with it fails). If the key may have leaked, revoke it and then purge your LMS's cache (Moodle: Site administration › Development › Purge caches). Rotate is for routine changes and keeps the old key valid for ${ROTATION_GRACE_DAYS} days.`
}

// What a failure with no sentence of the LMS's says.
const FAILED = {
  rotate: "The key couldn't be rotated.",
  revoke: "The key couldn't be revoked."
}

const TITLE = {
  rotate: ['Rotate the tool key?', 'Key rotated'],
  revoke: ['Revoke the tool key?', 'Key revoked']
}

export const LtiToolKeySection = ({ connectionId, toolKey, onChange, readOnly }) => {
  const [action, updateAction] = useState(null) // 'rotate' | 'revoke' while the dialog is open
  const [done, updateDone] = useState(null) // the new key once the change is made
  const [busy, updateBusy] = useState(false)
  const [error, updateError] = useState(null)

  const open = which => {
    updateAction(which)
    updateDone(null)
    updateError(null)
  }

  const close = () => {
    if (busy) return
    updateAction(null)
  }

  const confirm = async () => {
    updateBusy(true)
    updateError(null)
    try {
      const key = action === 'rotate' ? await lmsAdmin.rotateLtiToolKey(connectionId) : await lmsAdmin.revokeLtiToolKey(connectionId)
      onChange(key)
      updateDone(key)
    } catch (e) {
      updateError(e)
    } finally {
      updateBusy(false)
    }
  }

  return (
    <div className="form-section" data-testid="lti-tool-key-section">
      <div className="form-section-title">
        <span className="num">
          <LmsIcon name="key" style={{ fontSize: 9 }} />
        </span>
        <h2 className="label">Tool key</h2>
      </div>
      <div className="field">
        {toolKey ? (
          <React.Fragment>
            <div className="field-help" style={{ marginTop: 0 }}>
              Key ID (kid): <code className="lti-mono" data-testid="lti-tool-key-kid">{toolKey.kid}</code>
            </div>
            <div className="field-help" data-testid="lti-tool-key-issued">
              Created <span title={utcText(toolKey.issued_at)}>{dateTimeText(toolKey.issued_at)}</span>
            </div>
            {toolKey.previous_valid_until && (
              <div className="field-help" data-testid="lti-tool-key-grace">
                The previous key stays in the key set until{' '}
                <span title={utcText(toolKey.previous_valid_until)}>{dateTimeText(toolKey.previous_valid_until)}</span>.
              </div>
            )}
          </React.Fragment>
        ) : (
          <div className="field-help" style={{ marginTop: 0 }} data-testid="lti-tool-key-unreadable">
            This connection's tool key could not be read.
          </div>
        )}
        <div className="field-help">
          EducationXR signs what it sends your LMS with this key, and your LMS gets the public half from the keyset URL above. The
          private half is never shown. Rotate it on a schedule; revoke it if it may have leaked.
        </div>
        {!readOnly && (
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn btn-outline-secondary btn-sm" type="button" data-testid="lti-tool-key-rotate" onClick={() => open('rotate')}>
              Rotate key
            </button>
            <button className="btn btn-danger btn-sm" type="button" data-testid="lti-tool-key-revoke" onClick={() => open('revoke')}>
              Revoke key
            </button>
          </div>
        )}
      </div>

      {action && (
        <div className="lms-modal-scrim" data-testid={`lti-tool-key-${action}-modal`}>
          <div className="lms-modal" role="dialog" aria-modal="true" aria-label={TITLE[action][0]}>
            <div className="modal-head">
              <div className="icon">
                <LmsIcon name={action === 'rotate' ? 'rotate' : 'triangle-exclamation'} />
              </div>
              <div className="body">
                <h2>{done ? TITLE[action][1] : TITLE[action][0]}</h2>
              </div>
              <button className="close-btn" title="Close" type="button" onClick={close}>
                <LmsIcon name="xmark" />
              </button>
            </div>
            <div className="lms-modal-body">
              <p className="field-help" style={{ marginTop: 0 }} data-testid="lti-tool-key-warning">
                {WARNING[action]}
              </p>
              {done && (
                <p className="field-help" data-testid="lti-tool-key-new-kid-line">
                  The new key's ID (kid): <code className="lti-mono" data-testid="lti-tool-key-new-kid">{done.kid}</code>
                </p>
              )}
              {error && (
                <div className="error-banner" data-testid="lti-tool-key-error">
                  <LmsIcon name="circle-exclamation" />
                  <div>
                    <p className="title">{failureText(error, FAILED[action])}</p>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-foot">
              <span className="help" />
              {done ? (
                <button className="btn btn-light border btn-sm" type="button" data-testid="lti-tool-key-close" onClick={close}>
                  Close
                </button>
              ) : (
                <React.Fragment>
                  <button className="btn btn-light border btn-sm" type="button" data-testid="lti-tool-key-cancel" disabled={busy} onClick={close}>
                    Cancel
                  </button>
                  <button
                    className={`btn ${action === 'rotate' ? 'btn-outline-secondary' : 'btn-danger'} btn-sm`}
                    type="button"
                    data-testid="lti-tool-key-confirm"
                    disabled={busy}
                    onClick={confirm}
                  >
                    {action === 'rotate' ? 'Rotate key' : 'Revoke key'}
                  </button>
                </React.Fragment>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
